<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/PageHeader.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import Currency from '@/components/Currency.vue'
import { dashboardAPI } from '@/services/api.js'
import Loading from '@/components/Loading.vue'
import { viteConfig } from '@/services/utils.js'

const { t } = useI18n()
const router = useRouter()

// Anno/mese di rilascio del gioco
const gameFirstTimeframe = parseInt(viteConfig('GAME_FIRST_TIMEFRAME') || '202510')

// Riceve la prop type
const props = defineProps({
  type: {
    type: String,
    required: true
  },
  timeframe: {
    type: [String, Number],
    required: false
  }
})

const pageTitle = computed(() =>{
  if (props.type === 'month') {
    // Estrai il mese dal timeframe (formato YYYYMM)
    const month = props.timeframe ? parseInt(props.timeframe.toString().slice(-2)) : (new Date().getMonth() + 1)
    const year = props.timeframe ? parseInt(props.timeframe.toString().slice(0, 4)) : (new Date().getFullYear())
    const currentYear = new Date().getFullYear()
    
    const monthName = t('month.' + month)
    const yearSuffix = year !== currentYear ? ` ${year}` : ''
    
    return t('standings.topPlayersOfMonth', { month: monthName + yearSuffix })
  } else {
    return t('standings.topPlayers-' + props.type)
  }
})

const standings = ref([])
const loading = ref(true)
const page = ref(1)
const itemsPerPage = ref(25)
const totalItems = ref(0)
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))

// Verifica se il timeframe è valido
const timeframeValidation = computed(() => {
  if (props.type !== 'month' || !props.timeframe) {
    return { valid: true, reason: null }
  }
  
  const currentTimeframe = parseInt(props.timeframe.toString())
  
  // Controlla se è nel futuro
  if (currentTimeframe > currentYearMonth.value) {
    return { valid: false, reason: 'future' }
  }
  
  // Controlla se è prima del rilascio del gioco
  if (currentTimeframe < gameFirstTimeframe) {
    return { valid: false, reason: 'past' }
  }
  
  return { valid: true, reason: null }
})

const isValidTimeframe = computed(() => timeframeValidation.value.valid)

const displayRange = computed(() => {
  if (totalItems.value === 0) return ''
  const start = (page.value - 1) * itemsPerPage.value + 1
  const end = Math.min(page.value * itemsPerPage.value, totalItems.value)
  return `${start}-${end} ${t('common.of')} ${totalItems.value}`
})

const loadStandings = () => {
  // Non caricare se il timeframe non è valido
  if (!isValidTimeframe.value) {
    loading.value = false
    return
  }
  
  loading.value = true
  dashboardAPI.getStandings(props.type, props.timeframe, page.value, itemsPerPage.value)
    .then(res => {
      standings.value = res.data
      totalItems.value = res.total
      loading.value = false
    })
    .catch(err => {
      console.error(err)
      loading.value = false
    })
}

const onPageChange = (newPage) => {
  page.value = newPage
  loadStandings()
  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Navigation between months
const currentYearMonth = computed(() => {
  const now = new Date()
  return parseInt(`${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`)
})

const previousMonth = computed(() => {
  if (!props.timeframe) return null
  const year = parseInt(props.timeframe.toString().slice(0, 4))
  const month = parseInt(props.timeframe.toString().slice(-2))
  
  let prevYearMonth
  if (month === 1) {
    prevYearMonth = parseInt(`${year - 1}12`)
  } else {
    prevYearMonth = parseInt(`${year}${String(month - 1).padStart(2, '0')}`)
  }
  
  // Non mostrare il mese precedente se è prima del rilascio del gioco
  if (prevYearMonth < gameFirstTimeframe) {
    return null
  }
  
  return prevYearMonth.toString()
})

const nextMonth = computed(() => {
  if (!props.timeframe) return null
  const year = parseInt(props.timeframe.toString().slice(0, 4))
  const month = parseInt(props.timeframe.toString().slice(-2))
  
  const nextYearMonth = month === 12 ? parseInt(`${year + 1}01`) : parseInt(`${year}${String(month + 1).padStart(2, '0')}`)
  
  // Non permettere di andare oltre il mese corrente
  if (nextYearMonth > currentYearMonth.value) {
    return null
  }
  
  return nextYearMonth.toString()
})

const previousMonthName = computed(() => {
  if (!previousMonth.value) return ''
  const month = parseInt(previousMonth.value.slice(-2))
  return t('month.' + month)
})

const nextMonthName = computed(() => {
  if (!nextMonth.value) return ''
  const month = parseInt(nextMonth.value.slice(-2))
  return t('month.' + month)
})

const navigateToMonth = (yearMonth) => {
  router.push(`/standings/month/${yearMonth}`)
}

onMounted(() => {
  loadStandings()
})

// Watch for type and timeframe changes
watch([() => props.type, () => props.timeframe], () => {
  page.value = 1
  loadStandings()
  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' })
})
</script>

<template>
  <PageHeader :title="pageTitle" icon="mdi-trophy" />

  <v-container fluid>
    <Loading v-if="loading" />

    <!-- Invalid timeframe message -->
    <v-row v-else-if="!isValidTimeframe" justify="center" class="mt-8">
      <v-col cols="12" md="8" lg="6" class="text-center">
        <v-alert color="amber" variant="tonal" class="mb-4">
          <v-icon size="48" class="mb-2">mdi-calendar-alert</v-icon>
          <div class="text-h6 mb-2">
            {{ timeframeValidation.reason === 'future' ? $t('standings.futureTimeframe') : $t('standings.pastTimeframe') }}
          </div>
          <div class="text-body-2">
            {{ timeframeValidation.reason === 'future' ? $t('standings.futureTimeframeDescription') : $t('standings.pastTimeframeDescription') }}
          </div>
        </v-alert>
        <v-btn
          class="mt-8"
          variant="outlined"
          prepend-icon="mdi-calendar-today"
          @click="navigateToMonth(currentYearMonth)"
        >
          {{ $t('standings.goToCurrentMonth') }}
        </v-btn>
      </v-col>
    </v-row>

    <template v-else>
      <!-- No data state -->
      <v-row v-if="!standings || standings.length === 0" justify="center" class="my-8">
        <v-col cols="12" class="text-center">
          <v-icon size="64" color="grey">mdi-trophy-outline</v-icon>
          <p class="mt-4 text-medium-emphasis">{{ $t('dashboard.noDataAvailable') }}</p>
        </v-col>
      </v-row>

      <!-- Standings list -->
      <v-row v-else>
        <v-col cols="12">
          <!-- Results info -->
          <div v-if="totalItems > 0" class="text-caption text-medium-emphasis mb-2 text-center">
            {{ displayRange }}
          </div>

          <v-card>
            <v-card-text class="pa-0">
              <!-- Header row (hidden on mobile) -->
              <v-row class="d-none d-sm-flex px-4 py-3 font-weight-bold">
                <v-col cols="1" class="text-center">{{ $t('standings.position') }}</v-col>
                <v-col cols="4">{{ $t('standings.player') }}</v-col>
                <v-col cols="2" class="text-end">{{ $t('standings.winnings') }}</v-col>
                <v-col cols="2" class="text-center">{{ $t('standings.wonBets') }}</v-col>
                <v-col cols="3" class="text-center">{{ $t('standings.totalBets') }}</v-col>
              </v-row>

              <!-- Data rows -->
              <v-row
                v-for="(player, index) in standings"
                :key="player.id"
                class="px-4 py-3 align-center"
                :class="{ 'bg-alternating': index % 2 === 0 }"
              >
                <!-- Position -->
                <v-col cols="2" sm="1" class="text-center">
                  <v-chip
                    :color="index < 3 ? ['amber', 'blue-grey', 'orange'][index] : 'grey'"
                    size="small"
                  >
                    {{ player.position }}
                  </v-chip>
                </v-col>

                <!-- Avatar + Name -->
                <v-col cols="10" sm="4" class="d-flex align-center">
                  <UserAvatar 
                    :color="index < 3 ? 'primary' : 'grey'" 
                    :avatar="player.avatar" 
                    class="mr-3"
                  />
                  <span class="font-weight-medium">{{ player.name }}</span>
                </v-col>

                <!-- Winnings -->
                <v-col cols="6" sm="2" class="text-end">
                  <div class="d-sm-none text-caption text-medium-emphasis mb-1">
                    {{ $t('standings.winnings') }}
                  </div>
                  <Currency 
                    :amount="player.total_winnings" 
                    :class="{'text-success': player.total_winnings >= 0, 'text-error': player.total_winnings < 0}"
                  />
                </v-col>

                <!-- Won Bets -->
                <v-col cols="6" sm="2" class="text-center">
                  <div class="d-sm-none text-caption text-medium-emphasis mb-1">
                    {{ $t('standings.wonBets') }}
                  </div>
                  {{ player.won_bets }}<span class="d-sm-none">/{{ player.completed_bets }}</span>
                </v-col>

                <!-- Total Bets (Won/Completed) -->
                <v-col cols="12" sm="3" class="text-center d-none d-sm-block">
                  <div class="d-sm-none text-caption text-medium-emphasis mb-1">
                    {{ $t('standings.totalBets') }}
                  </div>
                  {{ player.completed_bets }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
            <v-pagination
              v-model="page"
              :length="totalPages"
              :total-visible="7"
              @update:modelValue="onPageChange"
              rounded="circle"
            />
          </div>
        </v-col>
      </v-row>

      <!-- Month navigation (only for month type) - always visible -->
      <v-row v-if="type === 'month'" justify="center" class="mt-8">
        <v-col cols="auto">
          <div class="d-flex align-center" style="gap: 16px;">
            <v-btn
              v-if="previousMonth"
              variant="outlined"
              prepend-icon="mdi-chevron-left"
              @click="navigateToMonth(previousMonth)"
            >
              {{ previousMonthName }}
            </v-btn>
            
            <v-btn
              v-if="nextMonth"
              variant="outlined"
              append-icon="mdi-chevron-right"
              @click="navigateToMonth(nextMonth)"
            >
              {{ nextMonthName }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<style scoped>
.bg-alternating {
  background-color: #181818 !important;
}
</style>
